import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CheckboxField,
  type Column,
  Details,
  DynamicDataList,
  type DynamicListColumn,
  DynamicListDataEntry,
  type DynamicListDataEntryProps,
  EmptyStation,
  type FormActionData,
  GenericField,
  InfoPanel,
  type InitialFormData,
  List,
  MessageBar,
  type Nullable,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SingleLineTextField,
  type StationMessage,
  createInputRenderer,
  formatDateTime,
  formatTitleCase,
  getFormDiff,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import * as EmailValidator from 'email-validator';
import { Field, useFormikContext } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  CreateEmailExclusionPatternDocument,
  DeleteEmailExclusionPatternDocument,
  type EmailExclusionPattern,
  EmailExclusionPatternsDocument,
  EmailExclusionPatternsOrderBy,
  type EmailExclusionPatternsQuery,
  type EmailExclusionPatternsQueryVariables,
  type IdpUserStore,
  IdpUserStoreCountDocument,
  type IdpUserStoreCountQuery,
  type IdpUserStoreCountQueryVariables,
  IdpUserStoreDocument,
  type IdpUserStoreQuery,
  type IdpUserStoreQueryVariables,
  UpdateEmailExclusionPatternDocument,
  type UpdateIdpUserStoreMutationVariables,
  UserCountAffectedByExclusionPatternDocument,
  type UserCountAffectedByExclusionPatternQuery,
  type UserCountAffectedByExclusionPatternQueryVariables,
  UserStoreCategory,
  useDeleteIdpUserStoreMutation,
  useUpdateIdpUserStoreMutation,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { getUserStoreCategoryText } from '../UserStores/UserStores';

type EmailExclusionPatternNode = Pick<
  EmailExclusionPattern,
  'emailPattern' | 'id' | 'affectedUserCount'
>;

type FormData = Nullable<
  UpdateIdpUserStoreMutationVariables['input']['patch']
> & {
  provisionedUserCount?: number;
  oAuthClientCount?: number;
  emailExclusionPatterns?: EmailExclusionPatternNode[];
};

type WebhookStatus = 'CONFIGURED' | 'PARTIALLY_CONFIGURED' | 'NOT_CONFIGURED';
interface WebhookData {
  hookName: string;
  hookType: string;
  status: WebhookStatus;
  url: string | undefined;
  isSecretSet: boolean | undefined;
}

const idpUserStoreSchema = Yup.object<ObjectSchemaDefinition<FormData>>({
  name: Yup.string().label('Name').required().max(200),
  category: Yup.string()
    .label('Category')
    .required()
    .default(UserStoreCategory.Management),
  enforceStrictPasswordPolicy: Yup.boolean()
    .label('Enforce Strict Password Policy')
    .required(),
});

export const UserStoreDetail: React.FC = () => {
  const { envId, userStoreId } = useParams<{
    envId: string;
    userStoreId: string;
  }>();

  const [data, setData] = useState<FormData>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const [stationMessage, setStationMessage] = useState<StationMessage>();
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );

          // Get IdpUserStore data
          const idpUserStoreResult = await envApolloClient.current.query<
            IdpUserStoreQuery,
            IdpUserStoreQueryVariables
          >({
            query: IdpUserStoreDocument,
            variables: {
              id: userStoreId,
            },
            fetchPolicy: 'no-cache',
          });

          // Get provisioned user count and OAuth client count
          const idpUserStoreCountData = await envApolloClient.current.query<
            IdpUserStoreCountQuery,
            IdpUserStoreCountQueryVariables
          >({
            query: IdpUserStoreCountDocument,
            variables: {
              idpUserCondition: {
                idpUserStoreId: userStoreId,
              },
              oAuthClientCondition: {
                idpUserStoreId: userStoreId,
              },
            },
            fetchPolicy: 'no-cache',
          });

          // Get email exclusion pattern
          const exclusionPatternData = await envApolloClient.current.query<
            EmailExclusionPatternsQuery,
            EmailExclusionPatternsQueryVariables
          >({
            query: EmailExclusionPatternsDocument,
            variables: {
              filter: {
                userStoreId: { equalTo: userStoreId },
              },
              orderBy: [EmailExclusionPatternsOrderBy.EmailPatternAsc],
            },
            fetchPolicy: 'no-cache',
          });

          const exclusionPatterns =
            exclusionPatternData.data.filtered?.nodes.map((node) => ({
              id: node.id,
              emailPattern: node.emailPattern,
              affectedUserCount: node.affectedUserCount ?? 0,
            }));

          setData({
            ...idpUserStoreResult.data.idpUserStore,
            provisionedUserCount:
              idpUserStoreCountData.data.idpUsers?.totalCount,
            oAuthClientCount:
              idpUserStoreCountData.data.oAuthClients?.totalCount,
            emailExclusionPatterns: exclusionPatterns ?? [],
          });
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  useEffect(() => {
    if (data?.category === UserStoreCategory.Management) {
      setStationMessage({
        type: 'info',
        title: `The users contained within this user-store can Sign-In to the Management System. However, a Management System administrator needs to set the user status to "Active" to allow them to use the system, or an Email Whitelist Pattern must be configured to automate this.`,
      });
    } else if (
      data?.category === UserStoreCategory.EndUser &&
      (!data?.userSignUpWebhook || !data?.forgotPasswordWebhook)
    ) {
      setStationMessage({
        type: 'warning',
        title: `Without the "User Sign-Up" and "Forgot Password" webhooks defined, an end-user will not be able to Sign-Up to a Front-End application nor perform a Password Reset.`,
      });
    }
  }, [data?.category, data?.userSignUpWebhook, data?.forgotPasswordWebhook]);

  const [updateIdpUserStore] = useUpdateIdpUserStoreMutation({
    client: envApolloClient.current,
    fetchPolicy: 'no-cache',
  });

  const { actions } = useActions(envId, userStoreId, envApolloClient.current);

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: InitialFormData<FormData>,
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      try {
        const patch = getFormDiff(formData, initialData.data);

        // Remove the emailExclusionPatterns from patch
        delete patch.emailExclusionPatterns;

        await updateIdpUserStore({
          variables: {
            input: {
              id: userStoreId,
              patch,
            },
          },
        });

        // Get the original list of exclusions from initial form data
        const originalPatterns = initialData.data?.emailExclusionPatterns ?? [];
        const currentPatterns = formData.emailExclusionPatterns ?? [];
        const currentIds = new Set(currentPatterns.map((e) => e.id));
        // Determine deleted exclusions
        const deletedItems = originalPatterns.filter(
          (item) => item.id && !currentIds.has(item.id),
        );

        // Perform deletion
        for (const item of deletedItems) {
          await envApolloClient.current?.mutate({
            mutation: DeleteEmailExclusionPatternDocument,
            variables: {
              input: { id: item.id },
            },
          });
        }

        // Create or update email exclusion pattern
        for (const exclusion of currentPatterns) {
          const { id, emailPattern } = exclusion;

          if (!id || id === '') {
            await envApolloClient.current?.mutate({
              mutation: CreateEmailExclusionPatternDocument,
              variables: {
                input: {
                  emailExclusionPattern: {
                    emailPattern,
                    userStoreId,
                    environmentId: envId,
                  },
                },
              },
            });
          } else {
            // Compare with the original before updating
            const original = originalPatterns.find((o) => o.id === id);
            if (original && original.emailPattern !== emailPattern) {
              await envApolloClient.current?.mutate({
                mutation: UpdateEmailExclusionPatternDocument,
                variables: {
                  input: {
                    id,
                    patch: {
                      emailPattern,
                    },
                  },
                },
              });
            }
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating user store or exclusion pattern:', error);
      }
    },
    [updateIdpUserStore, userStoreId, envId],
  );

  const Panel: React.FC<{
    provisionedUserCount?: number;
    oAuthClientCount?: number;
  }> = ({ provisionedUserCount, oAuthClientCount }) => {
    const { values } = useFormikContext<IdpUserStore>();
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="User Store ID">{values.id}</Paragraph>
          <Paragraph title="Created">
            {formatDateTime(values.createdAt)} by {values.createdBy}
          </Paragraph>
          <Paragraph title="Last Modified">
            {formatDateTime(values.updatedAt)} by {values.updatedBy}
          </Paragraph>
        </Section>
        <Section>
          <Paragraph title="Provisioned Users">
            {provisionedUserCount}
          </Paragraph>
          <Paragraph title="OAuth Clients">{oAuthClientCount}</Paragraph>
        </Section>
        <Section>
          <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
          <Paragraph title="Environment ID">{values.environmentId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<FormData>();
    const history = useHistory();
    const performDuplicateCheck = useRef(false);

    const formatEnumValueToTitleCase = (
      enumValue: string | undefined,
    ): string => {
      return formatTitleCase((enumValue ?? '').split('_').join(' ')) ?? '';
    };
    const webhooksColumns: Column<WebhookData>[] = [
      {
        propertyName: 'hookName',
        label: 'Name',
        sortable: false,
      },
      {
        propertyName: 'status',
        label: 'Configuration Status',
        sortable: false,
        render: (value) => formatEnumValueToTitleCase(value as string),
      },
    ];
    const webHooks: WebhookData[] = [
      {
        hookName: 'User Sign-Up',
        hookType: 'userSignUp',
        status: data?.userSignUpWebhookSecretIsSet
          ? 'CONFIGURED'
          : 'NOT_CONFIGURED',
        url: data?.userSignUpWebhook ?? '',
        isSecretSet: false,
      },
      {
        hookName: 'Forgot Password',
        hookType: 'forgotPassword',
        status: data?.resetPasswordWebhookSecretIsSet
          ? 'CONFIGURED'
          : 'NOT_CONFIGURED',
        url: data?.forgotPasswordWebhook ?? '',
        isSecretSet: false,
      },
      {
        hookName: 'Migration',
        hookType: 'migration',
        status: data?.migrationWebhookSecretIsSet
          ? 'CONFIGURED'
          : 'NOT_CONFIGURED',
        url: data?.migrationWebhook ?? '',
        isSecretSet: false,
      },
    ];

    const emailExclusionPatternColumns: DynamicListColumn<EmailExclusionPatternNode>[] =
      [
        {
          propertyName: 'emailPattern',
          label: 'Email',
          dataEntryRender: createInputRenderer(),
        },
        {
          propertyName: 'affectedUserCount',
          label: 'Affected Users No.',
          dataEntryRender: (value, error, onChange) =>
            createInputRenderer({
              type: 'number',
              placeholder: '#',
            })(value, error, onChange, true),
        },
      ];

    const wildcardReplacement = 'ax';

    const emailExclusionPatternValidationSchema = useMemo(
      () =>
        Yup.object<ObjectSchemaDefinition<EmailExclusionPatternNode>>({
          id: Yup.mixed().notRequired(),
          affectedUserCount: Yup.number().label('Affected Users No'),
          emailPattern: Yup.string()
            .required('Email is required')
            .label('Email')
            .max(255)
            .test(
              'email-pattern',
              'Email Pattern is not valid.',
              function (pattern: string | undefined) {
                if (pattern !== undefined) {
                  if (pattern === '*') {
                    return true;
                  }

                  // Prevent adjacent repetition of wildcard character '*'
                  if (pattern.indexOf('**') !== -1) {
                    return false;
                  }

                  // Replace wildcard character with a fixed replacement string, and validate the resulting email
                  const email = pattern.replace(/\*/g, wildcardReplacement);
                  return EmailValidator.validate(email);
                }
                return false;
              },
            ),
        }),
      [],
    );

    const onDDLChangeHandler = async (
      list: EmailExclusionPatternNode[],
    ): Promise<void> => {
      const transformed = await inputTransformer(
        list,
        userStoreId,
        envId,
        data?.emailExclusionPatterns ?? [],
      );

      setFieldValue('emailExclusionPatterns', transformed);
    };

    const CustomDynamicListDataEntryMemo = useMemo(() => {
      const CustomDynamicListDataEntry: React.FC<
        DynamicListDataEntryProps<EmailExclusionPatternNode>
      > = (props) => {
        const { onActionClicked, ...rest } = props;

        return (
          <DynamicListDataEntry
            {...rest}
            onActionClicked={async (data) => {
              // Add a temporary ID for the duplicate check
              if (data.id === undefined) {
                data.id = '';
              }

              performDuplicateCheck.current = true;

              if (
                await emailExclusionPatternValidationSchema.isValid(data, {
                  context: { duplicateCheck: true, mode: props.mode },
                })
              ) {
                onActionClicked && onActionClicked(data);

                performDuplicateCheck.current = false;
              }
            }}
          />
        );
      };

      return CustomDynamicListDataEntry;
    }, [emailExclusionPatternValidationSchema]);

    return (
      <>
        <Field name="name" label="Name" as={SingleLineTextField} />
        <Field
          name="category"
          label="Category"
          transform={(category: string) => getUserStoreCategoryText(category)}
          as={ReadOnlyField}
        />
        {values.category === UserStoreCategory.EndUser && (
          <>
            <GenericField label="Webhooks" name="webhooks">
              <List<WebhookData>
                columns={webhooksColumns}
                data={webHooks}
                onItemClicked={(data) => {
                  if (data.status === 'NOT_CONFIGURED') {
                    history.push(
                      `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/${data.hookType}/create`,
                    );
                  } else {
                    history.push(
                      `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/${data.hookType}/details`,
                    );
                  }
                }}
              />
            </GenericField>

            <Field
              name="enforceStrictPasswordPolicy"
              label="Enforce Strict Password Policy"
              tooltipContent={
                <>
                  <p>
                    The following password policy will be enforced during
                    sign-up of end-users.
                  </p>
                  <span>- min length: 10</span>
                  <span>- contains at least 3 of:</span>
                  <span>&emsp;- lowercase characters</span>
                  <span>&emsp;- uppercase characters</span>
                  <span>&emsp;- numbers</span>
                  <span>&emsp;- special characters</span>
                </>
              }
              as={CheckboxField}
            />
          </>
        )}

        <GenericField
          label="Email Exclusion Pattern"
          name="emailExclusionPatterns"
          tooltipContent="Email exclusion pattern supports the '*' (asterisk) wildcard character (valid examples: name@domain.com, *@domain.com, department@*.domain.com, department@*.domain.*)."
        >
          <DynamicDataList<EmailExclusionPatternNode>
            columns={emailExclusionPatternColumns}
            value={values.emailExclusionPatterns ?? []}
            allowReordering={false}
            allowRowDragging={false}
            allowNewData={true}
            stickyHeader={true}
            allowEditing={true}
            minimumWidth="300px"
            rowValidationSchema={emailExclusionPatternValidationSchema}
            customDataEntry={CustomDynamicListDataEntryMemo}
            onChange={onDDLChangeHandler}
            columnGap="10px"
          />
        </GenericField>
      </>
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="User Store Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="User Store Details"
          titleProperty="name"
          subtitle="User Store Details"
          actions={actions}
          validationSchema={idpUserStoreSchema}
          initialData={{
            data: data,
            loading,
            error,
          }}
          saveData={onSubmit}
          infoPanel={
            <Panel
              provisionedUserCount={data?.provisionedUserCount}
              oAuthClientCount={data?.oAuthClientCount}
            />
          }
          stationMessage={stationMessage}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useActions(
  envId: string,
  userStoreId: string,
  envApolloClient?: ApolloClient<Record<string, unknown>>,
) {
  const history = useHistory();

  const [deleteIdpUserStore] = useDeleteIdpUserStoreMutation({
    client: envApolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData[] = [
    {
      label: 'Provisioned Users',
      path: `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/provisionedUsers`,
    },
    {
      label: 'OAuth Clients',
      path: `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients`,
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await deleteIdpUserStore({
          variables: {
            input: {
              id: userStoreId,
            },
          },
          fetchPolicy: 'no-cache',
        });

        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores`,
        );
      },
    },
  ];

  return { actions } as const;
}

export const UserStoreDetailCrumb: BreadcrumbResolver = ({
  envId,
  userStoreId,
}) => {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
  );

  return async (): Promise<string> => {
    const response = await envApolloClient.query<IdpUserStoreQuery>({
      query: IdpUserStoreDocument,
      variables: { id: userStoreId },
      errorPolicy: 'ignore',
    });

    return response.data?.idpUserStore?.name || 'User Store Details';
  };
};

/**
 * Transforms current email exclusion patterns to the expected values in the DDL
 * @param nodes email exclusion patterns
 * @param idpUserStoreId user store ID
 * @param envId environment ID
 * @returns transformed email exclusion patterns nodes
 */
const inputTransformer = async (
  emailExclusionPatterns:
    | { id: string; emailPattern: string; affectedUserCount: number }[]
    | undefined,
  idpUserStoreId: string,
  envId: string,
  originalPatterns: EmailExclusionPatternNode[],
): Promise<EmailExclusionPatternNode[]> => {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL('/graphql', config.axAuthAdminBaseUrl).toString(),
  );

  const originalMap = new Map(
    originalPatterns.map((p) => [p.id, p.emailPattern]),
  );

  const lists = emailExclusionPatterns ?? [];
  const transformed = await Promise.all(
    lists.map(async (exclusionPattern) => {
      const originalPattern = originalMap.get(exclusionPattern.id);

      // Skip if pattern hasn't changed
      if (originalPattern === exclusionPattern.emailPattern) {
        return {
          ...exclusionPattern,
        };
      } else {
        const result = await envApolloClient.query<
          UserCountAffectedByExclusionPatternQuery,
          UserCountAffectedByExclusionPatternQueryVariables
        >({
          query: UserCountAffectedByExclusionPatternDocument,
          variables: {
            filter: {
              email: {
                like: exclusionPattern.emailPattern.replace(/\*/g, '%'),
              },
              idpUserStoreId: { equalTo: idpUserStoreId },
            },
          },
          fetchPolicy: 'no-cache',
        });

        const affectedUserCount = result.data.idpUsers?.totalCount ?? 0;

        return {
          id: exclusionPattern.id,
          emailPattern: exclusionPattern.emailPattern,
          affectedUserCount,
        };
      }
    }),
  );

  return transformed;
};
